import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "./auth";
import { getMyLeagues, rememberLeague, pickLeague, type MyLeague } from "./league";

// Which crew am I acting in right now?
//
// Season 0 assumed one crew per person, so the answer was wherever the URL
// happened to point and every screen worked it out again from useParams. That
// breaks in two ways once someone is in several crews. The small one is
// duplication. The bigger one is that no screen ever checked whether the crew in
// the URL was one you belong to: RLS meant you saw nothing, which is safe, but
// the screen just sat there empty with no explanation. Belonging is a question
// with a real answer and the app should give it.
//
// So the acting crew is resolved once, here, and validated against the crews you
// are actually in. Everything downstream reads it instead of re-deriving it.

export type CrewStatus = "loading" | "ready" | "no-crews" | "not-a-member";

export interface CrewState {
  crews: MyLeague[];
  crew: MyLeague | null;
  crewId: string | null;
  status: CrewStatus;
  // True when this person runs the acting crew. Roles are per crew, so this is
  // about the current one only and says nothing about any other.
  isHost: boolean;
  refresh: () => Promise<void>;
}

const Ctx = createContext<CrewState>({
  crews: [], crew: null, crewId: null, status: "loading", isHost: false,
  refresh: async () => {},
});

export function CrewProvider({ children }: { children: ReactNode }) {
  const { leagueId: routeCrew } = useParams();
  const { user } = useAuth();
  const [crews, setCrews] = useState<MyLeague[]>([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setCrews(await getMyLeagues(user.id));
    setLoaded(true);
  }, [user]);

  useEffect(() => { void load(); }, [load]);

  // The URL wins when it names a crew, because a link someone tapped should take
  // them where it says. It is only honoured once it is confirmed to be a crew
  // they are in.
  const wanted = routeCrew ?? pickLeague(crews);
  const crew = crews.find((c) => c.id === wanted) ?? null;

  let status: CrewStatus = "loading";
  if (loaded) {
    if (crew) status = "ready";
    else if (routeCrew) status = "not-a-member";
    else if (crews.length === 0) status = "no-crews";
    else status = "loading"; // crews exist but none picked yet; settles next tick
  }

  // Remember the acting crew so returning without a crew in the URL lands where
  // they left off rather than on whichever row the database returned first.
  useEffect(() => { if (crew) rememberLeague(crew.id); }, [crew?.id]);

  return (
    <Ctx.Provider
      value={{
        crews,
        crew,
        crewId: crew?.id ?? null,
        status,
        isHost: crew?.role === "commissioner",
        refresh: load,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export const useCrew = () => useContext(Ctx);
